import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { ErrorBoundary } from '@circuitry-ai/doc-ui';
import { AgentConverseLayout } from '@circuitry-ai/doc-chatadvisor';

export interface AgentExecutePanelProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    agentData: {
        aiworker_id: string;
        name?: string;
        id: string;
    } | null;
    entityId?: string;
    autoExecute?: boolean;
    isLoading?: boolean;
    executionTimestamp?: number;
}

// Wrap AgentConverseLayout with ErrorBoundary
const AgentConverseLayoutWithErrorBoundary = ErrorBoundary(AgentConverseLayout);

export function AgentExecutePanel({
    isOpen,
    onOpenChange,
    agentData,
    entityId,
    autoExecute = true,
    isLoading = false,
    executionTimestamp = 0
}: AgentExecutePanelProps) {

    // Use useState to track when the panel is opened with new data
    const [resetKey, setResetKey] = useState(0);

    // Optimized reset effect with proper validation
    useEffect(() => {
        if (isOpen && agentData?.aiworker_id && entityId && executionTimestamp > 0) {
            setResetKey(prev => prev + 1);
        }
    }, [isOpen, agentData?.aiworker_id, entityId, executionTimestamp]);

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <button
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => onOpenChange(false)}
                />
            )}

            {/* Right Side Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[900px] bg-content1 shadow-xl z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                {/* Close Button */}
                <button
                    onClick={() => onOpenChange(false)}
                    className="absolute top-4 right-4 z-10 p-2 hover:bg-default-100 rounded-full"
                >
                    <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
                </button>

                <div className="h-full overflow-hidden">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex flex-col items-center gap-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                <span className="text-default-500">Loading agent...</span>
                            </div>
                        </div>
                    ) : agentData ? (
                        <AgentConverseLayoutWithErrorBoundary
                            key={resetKey}
                            customAgent={agentData}
                            customInput={entityId}
                            autoExecute={autoExecute}
                            showAccordion={false}
                            hideTopHeader={true}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <Icon icon="mdi:alert-circle" className="w-12 h-12 text-default-400 mx-auto mb-2" />
                                <p className="text-default-500">No agent data available</p>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </>
    );
}
